import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';
import type { GroupResult, GroupRevision } from './access-groups';

/** 멤버 목록은 전화번호 원문 대신 저장된 마스킹 값만 제공한다. */
export interface AccessMember {
  id: string;
  group_id: string | null;
  device_id: string | null;
  user_id: string | null;
  pass_id: string | null;
  source_kind: 'ADMIN' | 'STAY' | 'GUEST_INVITATION';
  stay_id: string | null;
  room_id: string | null;
  valid_from: string;
  valid_until: string;
  revoked_at: string | null;
  revision: number;
  name: string | null;
  phone_masked: string | null;
}
export interface MemberPeriod extends GroupRevision {
  valid_from: string;
  valid_until: string;
}
export interface MemberInvite {
  command_id: string;
  id: string;
  valid_from: string;
  valid_until: string;
  user_id?: string | null;
  phone?: string | null;
  name?: string | null;
}
export interface MemberPage {
  items: AccessMember[];
  next_cursor: string | null;
}
export interface MemberResult extends GroupResult {
  message_sent?: boolean | null;
}
export interface MemberFilters {
  after?: string;
  limit?: number;
  property_id?: string;
  room_id?: string;
  user_id?: string;
  pass_id?: string;
  source_kind?: AccessMember['source_kind'];
}
const record = (x: unknown): x is Record<string, unknown> =>
  !!x && typeof x === 'object' && !Array.isArray(x);
const nullableUuid = (x: unknown): boolean => x === null || isUuid(x);
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '출입 멤버 응답을 확인할 수 없습니다.',
  });
export function parseAccessMember(x: unknown): AccessMember {
  const keys = [
    'id',
    'group_id',
    'device_id',
    'user_id',
    'pass_id',
    'source_kind',
    'stay_id',
    'room_id',
    'valid_from',
    'valid_until',
    'revoked_at',
    'revision',
    'name',
    'phone_masked',
  ];
  if (
    !record(x) ||
    Object.keys(x).length !== keys.length ||
    !keys.every((key) => Object.hasOwn(x, key)) ||
    !isUuid(x.id) ||
    !nullableUuid(x.group_id) ||
    !nullableUuid(x.device_id) ||
    (x.group_id === null) === (x.device_id === null) ||
    !nullableUuid(x.user_id) ||
    !nullableUuid(x.pass_id) ||
    (x.user_id === null) === (x.pass_id === null) ||
    !nullableUuid(x.stay_id) ||
    !nullableUuid(x.room_id) ||
    typeof x.source_kind !== 'string' ||
    !['ADMIN', 'STAY', 'GUEST_INVITATION'].includes(x.source_kind) ||
    !isOffsetDateTime(x.valid_from) ||
    !isOffsetDateTime(x.valid_until) ||
    !(x.revoked_at === null || isOffsetDateTime(x.revoked_at)) ||
    !Number.isSafeInteger(x.revision) ||
    Number(x.revision) < 1 ||
    !(x.name === null || typeof x.name === 'string') ||
    !(
      x.phone_masked === null ||
      (typeof x.phone_masked === 'string' && /^010-\*{4}-\d{4}$/.test(x.phone_masked))
    )
  )
    throw invalid();
  return x as unknown as AccessMember;
}
export function parseMemberPage(x: unknown): MemberPage {
  if (!record(x) || !Array.isArray(x.items) || !(x.next_cursor === null || isUuid(x.next_cursor)))
    throw invalid();
  return { items: x.items.map(parseAccessMember), next_cursor: x.next_cursor };
}
export function parseMemberResult(x: unknown, id: string): MemberResult {
  if (!record(x) || x.id !== id || !Number.isSafeInteger(x.revision) || Number(x.revision) < 1)
    throw invalid();
  if (
    Object.hasOwn(x, 'message_sent') &&
    x.message_sent !== null &&
    typeof x.message_sent !== 'boolean'
  )
    throw invalid();
  return {
    id,
    revision: Number(x.revision),
    ...(Object.hasOwn(x, 'message_sent') ? { message_sent: x.message_sent as boolean | null } : {}),
  };
}
export async function readAccessMembers(
  client: ServiceClient,
  filters: MemberFilters = {},
  signal?: AbortSignal,
): Promise<MemberPage> {
  return parseMemberPage(
    await client.get<unknown>('admin/access-members', {
      params: { ...filters },
      signal,
      headers: { 'Cache-Control': 'no-store' },
    }),
  );
}
export async function readGroupMembers(
  client: ServiceClient,
  groupId: string,
  signal?: AbortSignal,
  cursor?: string,
): Promise<MemberPage> {
  return parseMemberPage(
    await client.get<unknown>(`admin/access-groups/${groupId}/members`, {
      ...(cursor ? { params: { cursor } } : {}),
      signal,
      headers: { 'Cache-Control': 'no-store' },
    }),
  );
}
export async function inviteGroupMember(
  client: ServiceClient,
  groupId: string,
  body: MemberInvite,
): Promise<MemberResult> {
  return parseMemberResult(
    await client.post<unknown>(`admin/access-groups/${groupId}/members`, { json: body }),
    body.id,
  );
}
export async function updateAccessMember(
  client: ServiceClient,
  id: string,
  body: MemberPeriod,
): Promise<GroupResult> {
  return parseMemberResult(
    await client.patch<unknown>(`admin/access-members/${id}`, { json: body }),
    id,
  );
}
export async function revokeAccessMember(
  client: ServiceClient,
  id: string,
  body: GroupRevision,
): Promise<GroupResult> {
  return parseMemberResult(
    await client.delete<unknown>(`admin/access-members/${id}`, { json: body }),
    id,
  );
}
export async function resendGroupMember(
  client: ServiceClient,
  groupId: string,
  id: string,
  body: GroupRevision,
): Promise<MemberResult> {
  return parseMemberResult(
    await client.post<unknown>(`admin/access-groups/${groupId}/members/${id}/resend`, {
      json: body,
    }),
    id,
  );
}

/** 투숙 출입은 장비 상태만 표시하며 PIN을 포함하지 않는다. */
export interface StayAccess {
  items: Pick<
    AccessMember,
    | 'id'
    | 'group_id'
    | 'device_id'
    | 'user_id'
    | 'source_kind'
    | 'room_id'
    | 'valid_from'
    | 'valid_until'
    | 'revoked_at'
  >[];
  group_missing: boolean;
  doorlock: {
    device_id: string | null;
    status: 'NO_MEMBER' | 'MEMBER_CREATED' | 'REVOKED' | 'MULTIPLE_DEVICES';
  };
  unresolved: string[];
}
export async function readStayAccess(
  client: ServiceClient,
  stayId: string,
  signal?: AbortSignal,
): Promise<StayAccess> {
  const value = await client.get<unknown>(`admin/stays/${stayId}/access`, {
    signal,
    headers: { 'Cache-Control': 'no-store' },
  });
  if (
    !record(value) ||
    Object.keys(value).length !== 4 ||
    !Array.isArray(value.items) ||
    typeof value.group_missing !== 'boolean' ||
    !record(value.doorlock) ||
    Object.keys(value.doorlock).length !== 2 ||
    !nullableUuid(value.doorlock.device_id) ||
    typeof value.doorlock.status !== 'string' ||
    !['NO_MEMBER', 'MEMBER_CREATED', 'REVOKED', 'MULTIPLE_DEVICES'].includes(
      value.doorlock.status,
    ) ||
    !Array.isArray(value.unresolved) ||
    !value.unresolved.every((x: unknown) => typeof x === 'string')
  )
    throw invalid();
  const keys = [
    'id',
    'group_id',
    'device_id',
    'user_id',
    'source_kind',
    'room_id',
    'valid_from',
    'valid_until',
    'revoked_at',
  ];
  for (const row of value.items as unknown[]) {
    if (
      !record(row) ||
      Object.keys(row).length !== keys.length ||
      !keys.every((key) => Object.hasOwn(row, key)) ||
      !isUuid(row.id) ||
      !nullableUuid(row.group_id) ||
      !nullableUuid(row.device_id) ||
      !nullableUuid(row.user_id) ||
      !nullableUuid(row.room_id) ||
      typeof row.source_kind !== 'string' ||
      !['ADMIN', 'STAY', 'GUEST_INVITATION'].includes(row.source_kind) ||
      !isOffsetDateTime(row.valid_from) ||
      !isOffsetDateTime(row.valid_until) ||
      !(row.revoked_at === null || isOffsetDateTime(row.revoked_at))
    )
      throw invalid();
  }
  return value as unknown as StayAccess;
}
export async function reissueStayAccess(
  client: ServiceClient,
  stayId: string,
): Promise<{ status: 'PENDING' }> {
  const value = await client.post<unknown>(`admin/stays/${stayId}/access/reissue`);
  if (!record(value) || Object.keys(value).length !== 1 || value.status !== 'PENDING')
    throw invalid();
  return { status: 'PENDING' };
}
